/**
 * A folder's face: a 2×2 grid of its members' icons in one recessed squircle,
 * so a folder reads as "several apps" at a glance and its members are
 * recognisable without opening it — the Discord / iOS convention. Past four
 * members the last cell counts the rest ("+3"), so a big folder does not
 * pretend to hold four.
 *
 * Sized by one number so the rail (33px) and the mobile launcher (64px) draw
 * the same glyph at their own scales. A colour key tints the squircle.
 */
import { Globe } from 'lucide-react'
import type { FolderColorKey } from '@papan/types'
import { MiniappIcon } from '../MiniappIcon.js'
import type { RailApp } from './composeRailEntries.js'
import { folderColorClass } from './folderColors.js'

export const FOLDER_GLYPH_RAIL_SIZE = 33

export function FolderGlyph({ members, fallbackName, size = FOLDER_GLYPH_RAIL_SIZE, color = null, className = '' }: {
  members: RailApp[]
  fallbackName: string
  size?: number
  color?: FolderColorKey | null
  className?: string
}) {
  // 33 = 12·2 cells + 2 gap + 3·2 padding; everything scales from that.
  const k = size / FOLDER_GLYPH_RAIL_SIZE
  const cell = Math.round(12 * k)
  const gap = Math.max(1, Math.round(2 * k))
  const pad = Math.max(2, Math.round(3 * k))
  const overflow = members.length > 4 ? members.length - 3 : 0
  const shown = overflow ? members.slice(0, 3) : members.slice(0, 4)
  return (
    <span
      aria-hidden
      className={`grid grid-cols-2 pointer-events-none ${folderColorClass(color) || 'themed-bg-tertiary'} ${className}`}
      style={{ width: size, height: size, gap, padding: pad, borderRadius: Math.round(size * 0.27) }}
    >
      {shown.map(m => (
        <span key={`${m.kind}:${m.ref}`} className="flex items-center justify-center" style={{ width: cell, height: cell }}>
          {m.kind === 'miniapp'
            ? <MiniappIcon spec={m.icon ?? null} name={m.title || fallbackName} size={cell} refId={m.ref} rev={m.iconRev ?? null} />
            : <Globe size={Math.max(8, cell - 2)} className="themed-icon" />}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="flex items-center justify-center themed-text-muted font-semibold leading-none"
          style={{ width: cell, height: cell, fontSize: Math.max(7, Math.round(cell * 0.7)) }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
